import FFmpegService from "@/services/media/FFmpegService";
import { CodecValidator } from "@/services/media/CodecValidator";
import { OPFSService } from "@/services/media/OPFSService";

export interface RemuxProgress {
    totalDuration: number;
    processedTime: number;
    percent: number;
}

export class OPFSRemuxService {
    private readonly ffmpeg: FFmpegService;
    private readonly codecValidator: CodecValidator;
    private readonly opfsService: OPFSService;

    constructor(
        ffmpeg: FFmpegService,
        opfsService: OPFSService,
    ) {
        this.ffmpeg = ffmpeg;
        this.opfsService = opfsService;
        this.codecValidator = new CodecValidator();
    }

    public async remuxToOPFS(
        file: File,
        jobName: string,
        signal?: AbortSignal,
        onProgress?: (progress: RemuxProgress) => void
    ): Promise<File|null> {
        if (!this.ffmpeg.loaded) {
            await this.ffmpeg.load();
        }

        const inputDir = '/input';
        const inputPath = `/input/${file.name}`;
        const outputDevPath = '/dev/output.mp4';
        const tempJobName = `${jobName}.tmp`;

        try {
            await this.ffmpeg.createDir(inputDir);
            // @ts-ignore avoids copying file to MEMFS
            await this.ffmpeg.mount('WORKERFS', { files: [file] }, inputDir);

            const estimatedSize = Math.ceil(file.size * 1.2);
            await this.opfsService.allocateSpace(estimatedSize, jobName);

            const metadata = await this.ffmpeg.getMediaMetaData(inputPath)
            const duration = metadata.videos[0]?.duration || 0;

            await this.codecValidator.validateCodecSupport(metadata);

            // Write to temp file since the input may be read from jobName in OPFS
            await this.ffmpeg.mountOpfs(tempJobName, outputDevPath);

            const audioCodec = metadata.audio[0]?.codec || null;
            const needsAacBsf = audioCodec === 'aac';
            const bsfFlags = needsAacBsf ? ['-bsf:a', 'aac_adtstoasc'] : [];

            const cmd = [
                '-i', inputPath,
                '-c', 'copy',
                ...bsfFlags,
                '-movflags', '+faststart',
                '-f', 'mp4',
                outputDevPath
            ];

            console.log('[OPFSRemuxService] Starting direct OPFS remux...');

            if (onProgress && duration > 0) {
                this.ffmpeg.on('progress', (p: { time: number, progress: number }) => {
                    if (p.time !== undefined) {
                         onProgress({
                            totalDuration: duration,
                            processedTime: p.time,
                            percent: p.progress * 100
                        });
                    }
                });
            }

            await this.ffmpeg.exec(cmd, undefined, { signal });
            await this.ffmpeg.closeOpfs(outputDevPath);

            console.log('[OPFSRemuxService] Remux complete');

            if (onProgress && duration > 0) {
                onProgress({ totalDuration: duration, processedTime: duration, percent: 100 });
            }

            const tempFile = await this.opfsService.finalizeFile(tempJobName, file.name);
            await this.opfsService.saveFile(jobName, tempFile, file.name);
            await this.opfsService.deleteFile(tempJobName).catch(() => {});

            return await this.opfsService.finalizeFile(jobName, file.name);

        } catch (error) {
            await this.ffmpeg.closeOpfs(outputDevPath).catch(() => {});
            await this.opfsService.deleteFile(tempJobName).catch(() => {});
            await this.ffmpeg.deleteFile(inputPath).catch(() => {});

            if(!signal?.aborted) {
                console.error('[OPFSRemuxService] Error during remux:', error);
                throw error;
            } else {
                return null;
            }
        } finally {
            try {
                await this.ffmpeg.closeOpfs(outputDevPath).catch(() => {});
                await this.ffmpeg.unmount(inputDir);
                await this.ffmpeg.deleteDir(inputDir);
            } catch (e) {}
        }
    }
}
